import React, { useEffect, useRef, useState } from 'react';
import { PlotAssignment } from '../services/housingService';

interface PlotPosition {
  x: number;
  y: number;
  plotNumber: string;
}

interface HousingMapVisualizationProps {
  assignments: PlotAssignment[];
  plotAssignmentMap: Record<string, PlotAssignment>;
  onPlotClick?: (plotNumber: string) => void;
  userAssignment?: PlotAssignment | null;
}

// Plot positions from the JSON file
const PLOT_POSITIONS: PlotPosition[] = [
  { x: 2250, y: 1250, plotNumber: "0" },
  { x: 1635, y: 1445, plotNumber: "1" },
  { x: 1960, y: 1550, plotNumber: "2" },
  { x: 2430, y: 1100, plotNumber: "3" },
  { x: 1100, y: 850, plotNumber: "4" },
  { x: 1360, y: 1500, plotNumber: "5" },
  { x: 2370, y: 1450, plotNumber: "6" },
  { x: 1910, y: 830, plotNumber: "7" },
  { x: 2055, y: 1705, plotNumber: "8" },
  { x: 1840, y: 860, plotNumber: "9" },
  { x: 1865, y: 1645, plotNumber: "10" },
  { x: 2020, y: 1300, plotNumber: "11" },
  { x: 985, y: 1000, plotNumber: "12" },
  { x: 1430, y: 1350, plotNumber: "13" },
  { x: 1330, y: 1400, plotNumber: "14" },
  { x: 1960, y: 1400, plotNumber: "15" },
  { x: 2020, y: 1240, plotNumber: "16" },
  { x: 2330, y: 1250, plotNumber: "17" },
  { x: 2240, y: 1750, plotNumber: "18" },
  { x: 1270, y: 1000, plotNumber: "19" },
  { x: 2295, y: 1630, plotNumber: "20" },
  { x: 2530, y: 1500, plotNumber: "21" },
  { x: 2490, y: 1205, plotNumber: "22" },
  { x: 2520, y: 985, plotNumber: "23" },
  { x: 1250, y: 650, plotNumber: "24" },
  { x: 1020, y: 920, plotNumber: "25" },
  { x: 1650, y: 1530, plotNumber: "26" },
  { x: 1910, y: 760, plotNumber: "27" },
  { x: 1090, y: 775, plotNumber: "28" },
  { x: 1230, y: 850, plotNumber: "29" },
  { x: 1490, y: 1470, plotNumber: "30" },
  { x: 2360, y: 1000, plotNumber: "31" },
  { x: 2550, y: 1100, plotNumber: "32" },
  { x: 2130, y: 1580, plotNumber: "33" },
  { x: 1530, y: 920, plotNumber: "34" },
  { x: 1405, y: 1605, plotNumber: "35" },
  { x: 1240, y: 1405, plotNumber: "36" },
  { x: 1720, y: 1600, plotNumber: "37" },
  { x: 2295, y: 1545, plotNumber: "38" },
  { x: 1600, y: 830, plotNumber: "39" },
  { x: 1035, y: 835, plotNumber: "40" },
  { x: 2340, y: 1840, plotNumber: "41" },
  { x: 1740, y: 820, plotNumber: "42" },
  { x: 1240, y: 760, plotNumber: "43" },
  { x: 1820, y: 750, plotNumber: "44" },
  { x: 1620, y: 940, plotNumber: "45" },
  { x: 1830, y: 1100, plotNumber: "46" },
  { x: 1800, y: 1000, plotNumber: "47" },
  { x: 1460, y: 1220, plotNumber: "48" },
  { x: 1790, y: 1170, plotNumber: "49" },
  { x: 1350, y: 1050, plotNumber: "50" },
  { x: 1710, y: 1005, plotNumber: "51" },
  { x: 1390, y: 950, plotNumber: "52" },
  { x: 1520, y: 1250, plotNumber: "53" },
  { x: 1900, y: 1125, plotNumber: "54" },
];

const MAP_IMAGE_WIDTH = 3840;
const MAP_IMAGE_HEIGHT = 2560;

interface PlotPositionData {
  plotNumber: string;
  x: number;
  y: number;
}

const HousingMapVisualization: React.FC<HousingMapVisualizationProps> = ({
  assignments,
  plotAssignmentMap,
  onPlotClick,
  userAssignment,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapBackgroundRef = useRef<HTMLDivElement>(null);
  const [hoveredPlot, setHoveredPlot] = useState<string | null>(null);
  const [plotPositions, setPlotPositions] = useState<PlotPositionData[]>([]);
  const [zoomScale, setZoomScale] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  
  // Track initial container size for zoom detection
  const initialContainerSize = useRef<{ width: number; height: number } | null>(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect zoom/scale changes on mobile using Visual Viewport API
  useEffect(() => {
    if (!isMobile) return;

    // Use Visual Viewport API (best for mobile zoom detection)
    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        const scale = window.visualViewport.scale || 1;
        // Only update if scale changed significantly (threshold of 0.05)
        setZoomScale(prevScale => {
          if (Math.abs(scale - prevScale) > 0.05) {
            return scale;
          }
          return prevScale;
        });
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
      // Initial check
      handleVisualViewportChange();
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, [isMobile]);

  // Recalculate positions when zoom scale changes
  useEffect(() => {
    if (isMobile && zoomScale !== 1) {
      // Small delay to ensure viewport has updated
      const timer = setTimeout(() => {
        calculateMapLayout();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [zoomScale, isMobile]);

  // Calculate plot positions and grid data based on container size
  const calculateMapLayout = () => {
    if (!mapContainerRef.current) return;

    const mapRect = mapContainerRef.current.getBoundingClientRect();
    const containerWidth = mapRect.width;
    const containerHeight = mapRect.height;

    // Calculate the actual displayed image dimensions (maintaining aspect ratio)
    const imageAspectRatio = MAP_IMAGE_WIDTH / MAP_IMAGE_HEIGHT;
    const containerAspectRatio = containerWidth / containerHeight;

    let imageWidth: number, imageHeight: number, offsetX: number, offsetY: number;

    if (imageAspectRatio > containerAspectRatio) {
      // Image is wider than container
      imageWidth = containerWidth;
      imageHeight = containerWidth / imageAspectRatio;
      offsetX = 0;
      offsetY = (containerHeight - imageHeight) / 2;
    } else {
      // Image is taller than container
      imageHeight = containerHeight;
      imageWidth = containerHeight * imageAspectRatio;
      offsetX = (containerWidth - imageWidth) / 2;
      offsetY = 0;
    }

    // Calculate scale factor
    const scaleX = imageWidth / MAP_IMAGE_WIDTH;
    const scaleY = imageHeight / MAP_IMAGE_HEIGHT;

    // Calculate positions for all plots
    const positions: PlotPositionData[] = PLOT_POSITIONS.map((pos) => ({
      plotNumber: pos.plotNumber,
      x: offsetX + (pos.x * scaleX),
      y: offsetY + (pos.y * scaleY),
    }));

    setPlotPositions(positions);
  };

  // Handle window resize and initial load
  useEffect(() => {
    const handleResize = () => {
      calculateMapLayout();
      // Reset zoom scale on window resize (not zoom)
      if (isMobile) {
        setZoomScale(1);
        if (initialContainerSize.current) {
          initialContainerSize.current = null;
        }
      }
    };

    // Initial calculation after a short delay to ensure container is rendered
    const timer = setTimeout(() => {
      calculateMapLayout();
      if (isMobile && mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        initialContainerSize.current = { width: rect.width, height: rect.height };
      }
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);


  return (
    <div className="relative w-full" style={{ minHeight: '600px' }}>
      {/* Map Container */}
      <div
        ref={mapContainerRef}
        className="relative w-full bg-surface-light rounded-lg overflow-hidden"
        style={{ minHeight: '600px', height: '80vh' }}
      >
        {/* Map Background */}
        <div
          ref={mapBackgroundRef}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/images/housing-map.jpg)',
            backgroundSize: 'contain',
          }}
        >
          {/* Plot Markers */}
          {plotPositions.map((position) => {
            const plotNum = position.plotNumber;
            const assignment = plotAssignmentMap[plotNum];
            const isOccupied = !!assignment;
            const isHovered = hoveredPlot === plotNum;
            const isUserPlot = userAssignment && assignment && assignment.userId === userAssignment.userId;
            const isClickable = onPlotClick && !isOccupied; // Only allow clicking on unassigned plots
            
            // Debug logging for specific plots
            if (plotNum === "3" || plotNum === "18" || plotNum === "33") {
              console.log(`Plot ${plotNum}: assignment =`, assignment ? `${assignment.username} (${assignment.assignedPlot})` : 'null', 'map keys:', Object.keys(plotAssignmentMap));
            }

            // Determine background color
            let backgroundColor = 'rgba(255, 255, 255, 0.8)'; // Default: available (white)
            if (isUserPlot) {
              backgroundColor = 'rgba(59, 130, 246, 0.8)'; // User's plot (blue)
            } else if (isOccupied) {
              backgroundColor = 'rgba(76, 175, 80, 0.8)'; // Occupied (green)
            }

            // Determine border color
            let borderColor = '#333';
            if (isUserPlot) {
              borderColor = '#3b82f6'; // Blue border for user's plot
            } else if (isHovered && isClickable) {
              borderColor = '#8b5cf6'; // Purple border on hover for clickable plots
            }

            // Determine cursor
            let cursorStyle = 'default';
            if (isClickable) {
              cursorStyle = 'pointer';
            }

            // Determine text color
            let textColor = '#333';
            if (isOccupied || isUserPlot) {
              textColor = 'white';
            }

            return (
              <div
                key={plotNum}
                className={`house-plot absolute ${isOccupied ? 'occupied' : 'available'}`}
                style={{
                  // Use smaller markers on mobile by default to prevent overlap
                  // When zoomed, increase size slightly but keep spacing proportional to zoom
                  width: isMobile 
                    ? (zoomScale > 1 ? '24px' : '16px')
                    : '20px',
                  height: isMobile 
                    ? (zoomScale > 1 ? '24px' : '16px')
                    : '20px',
                  left: `${position.x}px`,
                  top: `${position.y}px`,
                  backgroundColor: backgroundColor,
                  border: `1px solid ${borderColor}`,
                  borderRadius: '3px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile 
                    ? (zoomScale > 1 ? '11px' : '8px')
                    : '10px',
                  fontWeight: 'bold',
                  color: textColor,
                  cursor: cursorStyle,
                  transition: 'all 0.2s ease',
                  zIndex: isHovered ? 20 : 10,
                  transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                }}
                onMouseEnter={() => setHoveredPlot(plotNum)}
                onMouseLeave={() => setHoveredPlot(null)}
                onClick={() => {
                  if (isClickable && onPlotClick) {
                    onPlotClick(plotNum);
                  }
                }}
              >
                {plotNum}
                
                {/* Tooltip */}
                <div
                  className="plot-info absolute"
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    marginTop: '-5px',
                    left: '50%',
                    transform: 'translateX(-50%) translateY(-100%)',
                    zIndex: 30,
                  }}
                >
                  {assignment
                    ? `Plot ${plotNum}: ${assignment.username}${assignment.characterName ? ` (${assignment.characterName})` : ''}${isUserPlot ? ' (Your Plot)' : ''}`
                    : `Plot ${plotNum}: Available${onPlotClick ? ' - Click to select' : ''}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div
        className="absolute top-4 right-4 bg-black/80 text-white p-4 rounded-lg text-xs z-50"
        style={{ minWidth: '150px' }}
      >
        <h3 className="font-bold mb-2">Legend</h3>
        <div className="flex items-center mb-2">
          <div className="w-3 h-3 bg-white/80 rounded mr-2"></div>
          <span>Available{onPlotClick ? ' (Clickable)' : ''}</span>
        </div>
        {userAssignment && (
          <div className="flex items-center mb-2">
            <div className="w-3 h-3 bg-blue-500/80 rounded mr-2"></div>
            <span>Your Plot</span>
          </div>
        )}
        <div className="flex items-center">
          <div className="w-3 h-3 bg-green-500/80 rounded mr-2"></div>
          <span>Occupied</span>
        </div>
      </div>
    </div>
  );
};

export default HousingMapVisualization;

